import { setWithExpiry, getWithExpiry } from '@/utilities/helpers';

/**
 * 
 *  Image Collection filtering functionality
 * 
 */
export class Galleries {

    /**
     * 
     * Contructor
     * 
     * @param {element} list - The list element to filter the suggestions into
     * @param {element} input - The input element to get the user query from
     * @param {element} element - The suggestion box element
     */
    constructor(grid, input, clear_filters, search_info) {

        this.query = '';
        this.image_collections = undefined;
        this.filtered_image_collections = [];
        this.grid = grid;
        this.page_limit = 48;
        this.current_page = 1;
        this.current_index = 0;
        this.pages = 0;
        this.filters = [];
        this.input = input;
        this.clear_filters = clear_filters;
        this.search_info = search_info;

        // Initialise the suggestion box interactions
        this.init();

    }

    /**
     * 
     * Initialise the suggestion box interactions
     * 
     */
    init() {

        // Fetch the list of image collections
        this.fetch();

        // Attach keyboard events to the input field
        this.input.addEventListener('keydown', (event) => {
            if (event.keyCode === 13) event.preventDefault();
        });

        // Attach keyboard events to the input field
        this.input.addEventListener('keyup', () => {
            // Set a timeout on the filter section 
            setTimeout(() => this.filter(), 50);
        });

        // Fetch the list of image collections on initial focus
        this.input.addEventListener('focus', () => {
            if (!this.image_collections) this.fetch();
        });

        // Fetch the list of image collections on initial focus
        this.clear_filters.addEventListener('click', (event) => {
            event.preventDefault();
            this.clear();
        });

        document.addEventListener('click', (event) => {

            if (event.target.matches('.images__wrap__grid__pagination__first') || event.target.matches('.images__wrap__grid__pagination__first img')) {
                event.preventDefault();
                this.paginate('previous');
            }

            if (event.target.matches('.images__wrap__grid__pagination__pages__item')) {
                event.preventDefault();
                this.paginate(parseInt(event.target.dataset.page))
            }

            if (event.target.matches('.images__wrap__grid__pagination__last') || event.target.matches('.images__wrap__grid__pagination__last img')) {
                event.preventDefault();
                this.paginate('next');
            }

        });

    }


    /**
     * 
     * Clear all filters and search parameters
     * 
     */
    clear() {

        // Clear the query
        this.query = '';

        // Clear the query input field
        this.input.value = '';

        // Show/hide the clear filters button bsed on search parameters being present
        this.clear_filters.classList[this.query.length ? 'add' : 'remove']('active');

        // Update the search info box
        this.search_info.innerHTML = (this.query.length) ? `Showing ${this.filtered_image_collections.length} of ${this.image_collections.length} topics` : '';

        // Show/hide the search info box based on search parameters being present
        this.search_info.classList[this.query.length ? 'add' : 'remove']('active');

        // Set a timeout on the filter section 
        setTimeout(() => this.filter(), 50);
    }


    /**
     * 
     *  Fetch a list of predefined search terms from a JSON file galleries
     * 
     */
    fetch() {

        if (getWithExpiry('dermnet_image_collections')) {
            this.image_collections = JSON.parse(getWithExpiry('dermnet_image_collections'));
            this.filter();
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/assets/___cache/image_collections.json?v=1.0.1')
                .then((response) => response.text())
                .then(data => {
                    setWithExpiry('dermnet_image_collections', data);
                    this.image_collections = JSON.parse(data);
                    this.filter();
                });
        }

    }


    /**
     * 
     * Filter a list of predefined search terms based on user input
     * 
     */
    filter() {

        // Reset the current page
        this.current_page = 0;

        // Update the query to match what a user has typed
        this.query = this.input.value.trim();

        // Filter the list of images based on the query 
        this.filtered_image_collections = (this.query.length) ? this.image_collections.filter(item => item.b?.toLowerCase().includes(this.query.toLowerCase())) : this.image_collections;

        // Show/hide the clear filters button based on search parameters being present
        this.clear_filters.classList[this.query.length || this.filters.length > 0 ? 'add' : 'remove']('active');

        // Update the search info box
        this.search_info.innerHTML = (this.query.length || this.filters.length > 0) ? `Showing ${this.filtered_image_collections.length} of ${this.image_collections.length} images` : '';

        // Show/hide the search info box based on search parameters being present
        this.search_info.classList[this.query.length || this.filters.length > 0 ? 'add' : 'remove']('active');

        // Render the list of suggestions
        this.render();

    }


    /**
     * 
     * Render out the list of suggestions
     * 
     * @param {string} html - HTML template initialiser
     */
    render(html = '') {

        // Clear the currently rendered letters array
        this.currently_rendered_letters = [];

        // Clear the grouped topics
        this.grouped_image_galleries = {};

        // Group the topics by their first letter in an array of arrays instead an object of arrays
        this.grouped_image_galleries = this.filtered_image_collections.reduce((r, a) => {
            if (isNaN(a.b.charAt(0))) {
                r[a.b.charAt(0)] = [...r[a.b.charAt(0)] || [], a];
            } else {
                r['#'] = [...r['#'] || [], a];
            }

            return r;
        }, {});

        // order the grouped topics by their first letter
        this.grouped_image_galleries = Object.keys(this.grouped_image_galleries).sort().reduce(
            (obj, key) => {
                obj[key] = this.grouped_image_galleries[key];
                return obj;
            },
            {}
        );

        // Empty out the grid
        this.grid.innerHTML = '';

        // If there are no topics then render the empty template
        if (this.filtered_image_collections.length === 0) this.grid.innerHTML = this.emptyTemplate();

        // Attach a scroll event to the window to render more letters
        window.addEventListener('scroll', () => {
            // If the user has scrolled to the bottom of the page then render more letters
            if (window.scrollY + window.innerHeight >= this.grid.offsetHeight + this.grid.offsetTop - 300) {

                // Render the next set of letters
                this.renderMoreLetters();
            }
        });

        // Render the first set of letters
        this.renderMoreLetters();

    }

    /**
     * 
     * Render out the results on a per letter basis
     * 
     */
    renderMoreLetters() {

        // Declare out HTML template
        let html = '';

        // Reset the count
        let count = 0;

        // Loop through the grouped topics and render them out
        for (const [letter, items] of Object.entries(this.grouped_image_galleries)) {
            if (letter && count < 1) {
                // If the letter is not in the currently rendered letters array then render it
                if (!this.currently_rendered_letters.includes(letter)) {

                    // Render the letter results out
                    html += this.template(letter, items);

                    // Increment the count
                    count++;

                    // Add the letter to the currently rendered letters array
                    this.currently_rendered_letters.push(letter);
                }
            }
        }

        // Append the HTML to the grid
        this.grid.innerHTML += html;

        // If we have only rendered 20 letters then we need to render more so it doesnt look like we only have a single letters worth of results
        if (this.grid.querySelectorAll('.topics__wrap__grid__column__list a').length < 10000) {
            if (this.filtered_image_collections.length > this.grid.querySelectorAll('.topics__wrap__grid__column__list a').length) {
                this.renderMoreLetters();
            }
        }

    }


    /**
     * 
     * Get a single pagination item
     * 
     * @param {number} page 
     * @param {number} currentPage 
     * @param {number} pages 
     * @returns {string} template 
     */
    paginationItem = (page, currentPage, pages) => {
        return /*html */`
        <button class="images__wrap__grid__pagination__pages__item ${(page === currentPage) ? 'active' : ''} ${(page >= pages) ? 'hidden' : ''}" data-page="${page}">${page + 1}</button>`
    }

    /**
     * 
     * Get the pagination template
     * 
     * @param {number} pages 
     * @param {number} currentPage 
     * @returns {string} template 
     */
    pagination(pages, currentPage) {
        return /*html */`
        <div class="images__wrap__grid__pagination"  style="${(pages === 1) ? `display:none;` : `display:flex;`}">
            <button class="images__wrap__grid__pagination__first">
                <img src="_resources/themes/mercury/dist/images/svg/arrow-left-medium-blue.svg" width="26" height="26" loading="lazy" alt="medium-arrrow-left-black">
            </button>
            ${[...Array((pages > 5) ? 5 : pages).keys()].map((item, index) => this.paginationItem((currentPage > 3 && pages > 5) ? index - 2 + currentPage : index, currentPage, pages)).join('')}
            <button class="images__wrap__grid__pagination__last">
                <img src="_resources/themes/mercury/dist/images/svg/arrow-right-medium-blue.svg" width="26" height="26" loading="lazy" alt="medium-arrrow-right-black">
            </button>
        </div>`
    }


    /**
     * 
     * Paginate to another page of results
     * 
     * @param {number} page 
     */
    paginate(page) {

        // Update the current page based on the clicked element
        if (page === 'previous') {
            (this.current_page > 0) ? this.current_page-- : this.current_page = 0;
        } else if (page === 'next') {
            (this.current_page < this.pages - 1) ? this.current_page++ : this.current_page = this.pages - 1;
        } else { this.current_page = page; }

        // Rrender the images
        this.render();

        // Scroll to the top of the grid
        window.scroll({
            behavior: 'smooth',
            left: 0,
            top: this.grid.offsetTop + 200
        });

    }


    /**
     * 
     * Mark a searched term in the list of images
     * 
     * @param {string} name 
     * @returns {string} template 
     */
    marked(name) {
        let regex = new RegExp(this.query, 'gi');
        return name.replace(regex, `<span class="marked">$&</span>`);
    }


    /**
    * 
    * No results template
    * 
    * @returns {string} template
    */
    empty() {
        return /*html*/`
        <div class="topics__wrap__grid__column topics__wrap__grid__column--full" style="padding: 2rem 1rem;">
            <div class="topics__wrap__grid__column__heading">
                <h3 class="h2">No results found</h3>
                <hr>
            </div>
        </div>`;
    }

    /**
     * 
     * @param {*} item 
     * @returns {string} template 
     */
    template(letter, items) {
        return /*html*/`
        <div class="topics__wrap__grid__column topics__wrap__grid__column--full" style="padding: 2rem 1rem;" data-letter="${letter}">
            <div class="topics__wrap__grid__column__heading">
                <h3 class="h2">${letter.toUpperCase()}</h3>
                <hr>
            </div>
            <div class="topics__wrap__grid__column__list">
                ${items.map(item => this.item(item.d, item.b)).join('')}
            </div>
        </div>`;

    }

    /**
      * 
      * Return a single topic item template
      * 
      * @param {object} item 
      * @returns {string} template 
      */
    item(link, title) {
        return /*html*/`
        <a href="${link}">${this.marked(title)}</a>`;
    }

}

/**
a:id
b:title
c:link
d:summary
e:thumbnail
*/